import { useState, useEffect } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Badge } from '../ui/badge';
import { templateClassifications } from '../../data/rulesData';
import { Plus, Mail, FileText, Database, Clock, HardDrive, Info, Globe, Building2 } from 'lucide-react';
import { toast } from 'sonner';
import type { TemplateType, TemplateClassification, TemplateScope } from '../../types/template.types';

interface NewTemplateDialogProps {
  open: boolean;
  onClose: () => void;
  onCreate: (data: {
    name: string;
    fileName: string;
    templateType: string;
    classification: string;
    content: string;
    commitMessage: string;
    subjectLine?: string;
    scope: TemplateScope;
  }) => void;
  isCreating?: boolean;
  defaultScope?: TemplateScope;
}

export default function NewTemplateDialog({ open, onClose, onCreate, isCreating, defaultScope = 'tenant' }: NewTemplateDialogProps) {
  const [name, setName] = useState('');
  const [type, setType] = useState<TemplateType>('email');
  const [classification, setClassification] = useState<TemplateClassification>('general');
  const [scope, setScope] = useState<TemplateScope>(defaultScope);

  useEffect(() => {
    if (open) setScope(defaultScope);
  }, [open, defaultScope]);

  const handleCreate = () => {
    if (!name.trim()) {
      toast.error('Template name is required');
      return;
    }
    const slug = name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
    const fileName = `${slug}.hbs`;
    onCreate({
      name,
      fileName,
      templateType: type,
      classification,
      content: `<!-- ${name} -->\n<html>\n<body>\n  <h1>{{title}}</h1>\n</body>\n</html>`,
      commitMessage: `create ${scope === 'base' ? 'base ' : ''}template: ${name}`,
      scope,
    });
    resetAndClose();
  };

  const resetAndClose = () => {
    setName('');
    setType('email');
    setClassification('general');
    setScope(defaultScope);
    onClose();
  };

  const typeOptions: { value: TemplateType; label: string; icon: React.ReactNode; color: string }[] = [
    { value: 'email', label: 'Email', icon: <Mail className="h-4 w-4" />, color: 'bg-blue-50 text-blue-700 border-blue-200' },
    { value: 'pdf', label: 'PDF', icon: <FileText className="h-4 w-4" />, color: 'bg-amber-50 text-amber-700 border-amber-200' },
    { value: 'ledes', label: 'LEDES', icon: <Database className="h-4 w-4" />, color: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  ];

  return (
    <Dialog open={open} onOpenChange={resetAndClose}>
      <DialogContent className="sm:max-w-lg max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base">
            <Plus className="h-4 w-4" />
            New Template
          </DialogTitle>
          <DialogDescription className="text-xs">
            Create a new template with type, classification, and delivery settings.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5 py-2">
          <div className="space-y-1.5">
            <Label className="text-xs font-medium">Scope</Label>
            <div className="flex gap-2">
              <button
                onClick={() => setScope('tenant')}
                className={`flex items-center gap-2 px-3 py-2 rounded-md border text-sm font-medium transition-all ${
                  scope === 'tenant'
                    ? 'bg-violet-50 text-violet-700 border-violet-200 border-current'
                    : 'bg-muted/30 text-muted-foreground border-border hover:bg-muted/50'
                }`}
              >
                <Building2 className="h-4 w-4" />
                Tenant
              </button>
              <button
                onClick={() => setScope('base')}
                className={`flex items-center gap-2 px-3 py-2 rounded-md border text-sm font-medium transition-all ${
                  scope === 'base'
                    ? 'bg-teal-50 text-teal-700 border-teal-200 border-current'
                    : 'bg-muted/30 text-muted-foreground border-border hover:bg-muted/50'
                }`}
              >
                <Globe className="h-4 w-4" />
                Base
              </button>
            </div>
            {scope === 'base' && (
              <p className="text-[11px] text-muted-foreground flex items-center gap-1">
                <Info className="h-3 w-3" />
                Base templates are shared across all tenants
              </p>
            )}
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs font-medium">Template Name</Label>
            <Input placeholder="e.g. Trust Request Reminder" value={name} onChange={e => setName(e.target.value)} className="h-9 text-sm" />
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs font-medium">Type</Label>
            <div className="flex gap-2">
              {typeOptions.map(opt => (
                <button
                  key={opt.value}
                  onClick={() => setType(opt.value)}
                  className={`flex items-center gap-2 px-3 py-2 rounded-md border text-sm font-medium transition-all ${
                    type === opt.value
                      ? `${opt.color} border-current`
                      : 'bg-muted/30 text-muted-foreground border-border hover:bg-muted/50'
                  }`}
                >
                  {opt.icon}
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs font-medium">Classification</Label>
            <Select value={classification} onValueChange={v => setClassification(v as TemplateClassification)}>
              <SelectTrigger className="h-9 text-sm"><SelectValue /></SelectTrigger>
              <SelectContent>
                {templateClassifications.map(c => (
                  <SelectItem key={c.value} value={c.value} className="text-sm">{c.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {name && (
            <div className="rounded-md bg-muted/50 p-3 text-xs space-y-1.5">
              <p className="font-medium text-foreground">Template will be created as:</p>
              <p className="font-mono text-muted-foreground">
                {scope === 'base' ? 'base/' : ''}{type}/{name.toLowerCase().replace(/[^a-z0-9]+/g, '-')}.hbs
              </p>
              <div className="flex items-center gap-2 mt-2 flex-wrap">
                {scope === 'base' && (
                  <Badge variant="secondary" className="text-[10px] bg-teal-100 text-teal-800">BASE</Badge>
                )}
                <Badge variant="secondary" className="text-[10px] bg-gray-100 text-gray-800">{type.toUpperCase()}</Badge>
                <Badge variant="outline" className="text-[10px]">
                  {templateClassifications.find(c => c.value === classification)?.label}
                </Badge>
              </div>
            </div>
          )}
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" size="sm" onClick={resetAndClose} className="text-xs">Cancel</Button>
          <Button size="sm" disabled={!name.trim() || isCreating} onClick={handleCreate} className="text-xs gap-1.5">
            <Plus className="h-3 w-3" />
            {isCreating ? 'Creating...' : 'Create Template'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
